import type { Locale, Total } from "./types.js";

const TAG: Record<Locale, string> = { es: "es-ES", en: "en" };
const cache = new Map<string, Intl.NumberFormat>();

export const intlTag = (l: Locale): string => TAG[l] ?? TAG.es;

function formatter(locale: Locale, currency: string, long: boolean, integer: boolean): Intl.NumberFormat | null {
  const key = `${locale}|${currency}|${long ? 1 : 0}|${integer ? 1 : 0}`;
  let f = cache.get(key);
  if (!f) {
    try {
      f = new Intl.NumberFormat(intlTag(locale), {
        style: "currency",
        currency,
        ...(long ? { currencyDisplay: "name" as const } : {}),
        ...(long && integer ? { minimumFractionDigits: 0 } : {}),
      });
    } catch {
      return null; // código de moneda inválido
    }
    cache.set(key, f);
  }
  return f;
}

/** Decimales de la moneda (JPY = 0, EUR = 2...). */
export function fractionDigits(currency: string): number {
  const f = formatter("en", currency, false, false);
  return f ? f.resolvedOptions().maximumFractionDigits ?? 2 : 2;
}

/** Redondea un importe a los decimales de su moneda (evita 0.1 + 0.2 = 0.30000000000000004). */
export function roundMoney(amount: number, currency: string): number {
  const d = fractionDigits(currency);
  const m = 10 ** d;
  return Math.round((amount + Number.EPSILON) * m) / m;
}

/**
 * `long` produce la versión para lectores de pantalla: "48 euros" en vez de "48,00 €".
 */
export function formatMoney(locale: Locale, amount: number, currency: string, long = false): string {
  const f = formatter(locale, currency, long, Number.isInteger(amount));
  if (!f) return `${amount.toFixed(2)} ${currency}`;
  return f.format(amount);
}

/** mm:ss (redondea hacia arriba los segundos; nunca negativo). */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m < 10 ? "0" : ""}${m}:${s < 10 ? "0" : ""}${s}`;
}

export interface PricedLine {
  price: number | null;
  currency: string | null;
  qty: number;
}

/**
 * Suma un pedido. Devuelve null si no hay líneas, si alguna no tiene precio o si mezcla monedas:
 * un total parcial sería engañoso.
 */
export function sumTotal(lines: PricedLine[]): Total | null {
  if (lines.length === 0) return null;
  let currency: string | null = null;
  let minor = 0;
  for (const l of lines) {
    if (l.price === null || l.currency === null || !Number.isFinite(l.price)) return null;
    if (currency === null) currency = l.currency;
    else if (currency !== l.currency) return null;
    const d = fractionDigits(l.currency);
    minor += Math.round(l.price * 10 ** d) * l.qty;
  }
  if (currency === null) return null;
  return { amount: minor / 10 ** fractionDigits(currency), currency };
}
